import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { FiAlertTriangle, FiArrowRight, FiCopy, FiEye, FiEyeOff, FiArrowLeft } from 'react-icons/fi';
import * as bip39 from 'bip39';
import { useWallet } from '../context/WalletContext';
import '../styles/CreateWalletPage.css';

const CreateWalletPage = () => {
  const [mnemonic, setMnemonic] = useState('');
  const [showPhrase, setShowPhrase] = useState(false);
  const [copied, setCopied] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const newMnemonic = bip39.generateMnemonic(128);
    setMnemonic(newMnemonic);
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(mnemonic);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleContinue = () => {
    if (!confirmed) return;
    sessionStorage.setItem('temp_mnemonic', mnemonic);
    navigate('/confirm-phrase', { state: { mnemonic } });
  };

  const words = mnemonic.split(' ');

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.05 }
    }
  };

  const wordVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1 }
  };

  return (
    <div className="page-container">
      <div className="aurora-bg">
        <div className="aurora-blob aurora-1" />
        <div className="aurora-blob aurora-2" />
        <div className="grid-overlay" />
      </div>

      <motion.div
        className="content-wrapper"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="header-nav">
          <button className="nav-back-btn" onClick={() => navigate(-1)}>
            <FiArrowLeft />
          </button>
          <h1 className="page-title">Secret Phrase</h1>
        </div>

        <p className="page-subtitle">
          Write down these 12 words in order. You'll need them to recover your wallet.
        </p>

        <div className="glass-card">
          <div className="phrase-header">
            <div className="warning-badge">
              <FiAlertTriangle />
              <span>Keep this safe!</span>
            </div>
            <div className="phrase-controls">
              <button onClick={() => setShowPhrase(!showPhrase)} className="icon-btn">
                {showPhrase ? <FiEyeOff /> : <FiEye />}
              </button>
              <button onClick={handleCopy} className="icon-btn">
                {copied ? <span className="copy-text">Copied!</span> : <FiCopy />}
              </button>
            </div>
          </div>

          <motion.div
            className={`words-grid ${!showPhrase ? 'blurred' : ''}`}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {words.map((word, index) => (
              <motion.div
                key={index}
                className="word-item"
                variants={wordVariants}
                whileHover={{ scale: 1.05 }}
              >
                <span className="word-num">{index + 1}</span>
                <span className="word-text">{showPhrase ? word : '•••••'}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>

        <div className="confirmation-section">
          <label className="checkbox-wrapper">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
            />
            <div className="custom-checkbox" />
            <span className="checkbox-text">I have written down my recovery phrase safely</span>
          </label>
        </div>

        <div className="actions-footer">
          <button
            className="crystal-button btn-primary"
            onClick={handleContinue}
            disabled={!confirmed}
            style={{ opacity: confirmed ? 1 : 0.5, pointerEvents: confirmed ? 'auto' : 'none' }}
          >
            <span>Verify Phrase</span>
            <FiArrowRight />
            <div className="btn-shine" />
          </button>
        </div>

      </motion.div>
    </div>
  );
};

export default CreateWalletPage;
